import { useState, useCallback, useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import { useAssets } from "../../hooks/use-assets";
import { useAssetLinks } from "../../hooks/use-asset-links";
import {
  AssetType,
  AssetStatus,
  ASSET_TYPE_LABELS,
  ASSET_TYPE_CONFIG,
  Asset,
  AssetFilter,
  CreateAssetInput,
  UpdateAssetInput,
} from "../../types/asset-types";
import { LIST_ASSET_LINKS } from "../../services/graphql/asset-link-queries";
import { AssetFormModal } from "../../components/assets/asset-form-modal";
import { AssetSearchFilters } from "../../components/assets/asset-search-filters";
import { AssetExport } from "../../components/assets/asset-export";
import { AssetDetailModal } from "../../components/assets/asset-detail-modal";
import { Dialog } from "../../components/ui/dialog";

export function AssetsPage() {
  const client = useApolloClient();
  const [filter, setFilter] = useState<AssetFilter>({});
  const [viewMode, setViewMode] = useState<"grid" | "list">("list"); // Default to list view
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType | null>(
    null
  );
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Bulk selection and actions
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(
    new Set()
  );
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showArchiveConfirmModal, setShowArchiveConfirmModal] = useState(false);
  const [bulkActionInProgress, setBulkActionInProgress] = useState(false);
  const [bulkQRGenerationInProgress, setBulkQRGenerationInProgress] =
    useState(false);
  const [qrGenerationProgress, setQRGenerationProgress] = useState<{
    total: number;
    completed: number;
    skipped: number;
    errors: number;
  }>({ total: 0, completed: 0, skipped: 0, errors: 0 });

  const {
    assets,
    loading,
    error,
    total,
    hasMore,
    refetch,
    loadMore,
    clearFilter,
    createAsset,
    updateAsset,
    deleteAsset,
    archiveAsset,
  } = useAssets({ filter, limit: 20 });

  // Handle filter changes
  const handleFilterChange = useCallback((newFilter: AssetFilter) => {
    setFilter(newFilter);
  }, []);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setFilter({});
    clearFilter();
  }, [clearFilter]);

  // Handle export
  const handleExport = useCallback(() => {
    setShowExportModal(true);
  }, []);

  // Handle create asset
  const handleCreateAsset = useCallback((type?: AssetType) => {
    setSelectedAssetType(type || null);
    setShowCreateModal(true);
  }, []);

  // Handle asset creation/update submission
  const handleAssetSubmit = useCallback(
    async (data: CreateAssetInput | UpdateAssetInput) => {
      try {
        if (selectedAsset) {
          // Update existing asset
          await updateAsset(selectedAsset.id, data as UpdateAssetInput);
        } else {
          // Create new asset
          await createAsset(data as CreateAssetInput);
        }
        setShowCreateModal(false);
        setSelectedAsset(null);
        setSelectedAssetType(null);
        refetch(); // Refresh the list
      } catch (error) {
        console.error("Failed to save asset:", error);
        throw error; // Re-throw to let form handle the error
      }
    },
    [selectedAsset, createAsset, updateAsset, refetch]
  );

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setShowCreateModal(false);
    setSelectedAsset(null);
    setSelectedAssetType(null);
  }, []);

  // Handle view asset
  const handleViewAsset = useCallback((asset: Asset) => {
    setSelectedAsset(asset);
    setShowDetailModal(true);
  }, []);

  // Handle edit asset from detail modal
  const handleEditAsset = useCallback((asset: Asset) => {
    setSelectedAsset(asset);
    setShowDetailModal(false);
    setShowCreateModal(true);
  }, []);

  // Handle detail modal close
  const handleDetailModalClose = useCallback(() => {
    setShowDetailModal(false);
    setSelectedAsset(null);
  }, []);

  // Handle asset selection
  const handleAssetSelection = useCallback(
    (assetId: string, isSelected: boolean) => {
      setSelectedAssetIds((prev) => {
        const newSelection = new Set(prev);
        if (isSelected) {
          newSelection.add(assetId);
        } else {
          newSelection.delete(assetId);
        }
        return newSelection;
      });
    },
    []
  );

  // Handle select all assets
  const handleSelectAllAssets = useCallback(
    (isSelected: boolean) => {
      if (isSelected) {
        const allIds = assets.map((asset) => asset.id);
        setSelectedAssetIds(new Set(allIds));
      } else {
        setSelectedAssetIds(new Set());
      }
    },
    [assets]
  );

  // Handle delete assets
  const handleDeleteAssets = useCallback(async () => {
    try {
      setBulkActionInProgress(true);
      const selectedIds = Array.from(selectedAssetIds);

      // Delete assets one by one
      await Promise.all(selectedIds.map((id) => deleteAsset(id)));

      // Clear selection and refresh
      setSelectedAssetIds(new Set());
      setShowDeleteConfirmModal(false);
      refetch();
    } catch (error) {
      console.error("Failed to delete assets:", error);
    } finally {
      setBulkActionInProgress(false);
    }
  }, [selectedAssetIds, deleteAsset, refetch]);

  // Handle archive assets
  const handleArchiveAssets = useCallback(async () => {
    try {
      setBulkActionInProgress(true);
      const selectedIds = Array.from(selectedAssetIds);

      // Archive assets one by one
      await Promise.all(selectedIds.map((id) => archiveAsset(id)));

      // Clear selection and refresh
      setSelectedAssetIds(new Set());
      setShowArchiveConfirmModal(false);
      refetch();
    } catch (error) {
      console.error("Failed to archive assets:", error);
    } finally {
      setBulkActionInProgress(false);
    }
  }, [selectedAssetIds, archiveAsset, refetch]);

  // Handle bulk QR download
  const handleBulkDownloadQRCodes = useCallback(async () => {
    try {
      setBulkActionInProgress(true);
      const selectedAssets = assets.filter((asset) =>
        selectedAssetIds.has(asset.id)
      );

      // Get asset links for selected assets that have QR codes
      const assetsWithQRCodes = [];

      for (const asset of selectedAssets) {
        try {
          // Query asset links for this asset
          const { data } = await client.query({
            query: LIST_ASSET_LINKS,
            variables: { assetId: asset.id, limit: 1 },
            fetchPolicy: "network-only",
          });

          if (data?.listAssetLinks?.items?.length > 0) {
            const assetLink = data.listAssetLinks.items[0];
            if (assetLink.qrCodes && assetLink.qrCodes.length > 0) {
              assetsWithQRCodes.push({
                asset,
                assetLink,
                qrCode: assetLink.qrCodes[0], // Use the first QR code
              });
            }
          }
        } catch (error) {
          console.warn(`Failed to get asset links for ${asset.name}:`, error);
        }
      }

      if (assetsWithQRCodes.length === 0) {
        alert(
          "No QR codes found for the selected assets. Please generate QR codes first."
        );
        return;
      }

      // Download QR codes
      for (const { asset, assetLink, qrCode } of assetsWithQRCodes) {
        try {
          const response = await fetch(qrCode.downloadUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch QR code: ${response.status}`);
          }

          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);

          // Create filename: NAME_SHORTCODE (no spaces)
          const filename = `${asset.name.replace(/\s+/g, "_")}_${
            assetLink.shortCode || "QR"
          }.svg`;

          // Create download link
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          // Small delay between downloads to avoid browser blocking
          await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (error) {
          console.error(`Failed to download QR code for ${asset.name}:`, error);
        }
      }

      // Clear selection
      setSelectedAssetIds(new Set());
    } catch (error) {
      console.error("Failed to bulk download QR codes:", error);
      alert("Failed to download QR codes. Please try again.");
    } finally {
      setBulkActionInProgress(false);
    }
  }, [selectedAssetIds, assets, client]);

  // Handle bulk QR code generation
  const handleBulkGenerateQRCodes = useCallback(async () => {
    if (selectedAssetIds.size === 0) return;

    setBulkQRGenerationInProgress(true);
    const initialProgress = {
      total: selectedAssetIds.size,
      completed: 0,
      skipped: 0,
      errors: 0,
    };
    setQRGenerationProgress(initialProgress);

    const selectedAssets = assets.filter((asset) =>
      selectedAssetIds.has(asset.id)
    );

    // Import Apollo client for direct GraphQL calls
    const { getApolloClient } = await import("../../services/apollo-client");
    const { LIST_ASSET_LINKS, GENERATE_ASSET_LINK, GENERATE_QR_CODE } =
      await import("../../services/graphql/asset-link-queries");

    const client = getApolloClient();
    let completed = 0;
    let skipped = 0;
    let errors = 0;

    for (const asset of selectedAssets) {
      try {
        console.log(`Processing QR code generation for asset: ${asset.id}`);

        // Step 1: Check if asset has existing asset links
        const { data: assetLinksData } = await client.query({
          query: LIST_ASSET_LINKS,
          variables: {
            assetId: asset.id,
            limit: 10,
          },
          fetchPolicy: "network-only", // Always fetch fresh data
        });

        const assetLinks = assetLinksData?.listAssetLinks?.items || [];

        if (assetLinks.length === 0) {
          // Step 2: Generate asset link if none exists
          console.log(`No asset links found for ${asset.id}, generating...`);
          const { data: linkData } = await client.mutate({
            mutation: GENERATE_ASSET_LINK,
            variables: {
              input: {
                assetId: asset.id,
                metadata: {
                  description: `Trackable link for ${asset.name}`,
                },
                generateQRCode: false,
              },
            },
          });

          if (linkData?.generateAssetLink) {
            assetLinks.push(linkData.generateAssetLink);
          }
        }

        // Step 3: Check each asset link for existing QR codes and generate if needed
        let hasGeneratedQR = false;
        for (const assetLink of assetLinks) {
          const existingQRCodes = assetLink.qrCodes || [];

          if (existingQRCodes.length === 0) {
            // Generate QR code for this asset link
            console.log(`Generating QR code for asset link: ${assetLink.id}`);

            await client.mutate({
              mutation: GENERATE_QR_CODE,
              variables: {
                input: {
                  assetLinkId: assetLink.id,
                  options: {
                    size: "MEDIUM",
                    includeActivQMSLogo: true,
                    formats: ["SVG"],
                  },
                },
              },
            });

            hasGeneratedQR = true;
          } else {
            console.log(
              `Asset link ${assetLink.id} already has QR codes, skipping`
            );
          }
        }

        if (hasGeneratedQR) {
          completed++;
        } else {
          skipped++;
        }

        // Update progress
        setQRGenerationProgress({
          total: selectedAssetIds.size,
          completed,
          skipped,
          errors,
        });
      } catch (error) {
        console.error(
          `Failed to generate QR code for asset ${asset.id}:`,
          error
        );
        errors++;
        setQRGenerationProgress({
          total: selectedAssetIds.size,
          completed,
          skipped,
          errors,
        });
      }
    }

    setBulkQRGenerationInProgress(false);
    setSelectedAssetIds(new Set()); // Clear selection after completion

    // Show completion message
    console.log(
      `Bulk QR generation completed: ${completed} generated, ${skipped} skipped, ${errors} errors`
    );

    // Show user-friendly notification
    if (completed > 0) {
      console.log(`✅ Successfully generated QR codes for ${completed} assets`);
    }
    if (skipped > 0) {
      console.log(`⏭️ Skipped ${skipped} assets that already had QR codes`);
    }
    if (errors > 0) {
      console.log(`❌ Failed to generate QR codes for ${errors} assets`);
    }
  }, [selectedAssetIds, assets]);

  // Calculate selection stats
  const selectionStats = useMemo(() => {
    return {
      count: selectedAssetIds.size,
      allSelected: assets.length > 0 && selectedAssetIds.size === assets.length,
      anySelected: selectedAssetIds.size > 0,
    };
  }, [selectedAssetIds, assets]);

  // Get asset file size display
  const getAssetFileSize = (asset: Asset): string => {
    if (asset.files.length === 0) return "No files";
    if (asset.files.length === 1) {
      return formatFileSize(asset.files[0]?.size || 0);
    }
    const totalSize = asset.files.reduce((sum, file) => sum + file.size, 0);
    return `${formatFileSize(totalSize)} (${asset.files.length} files)`;
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // Get asset type icon
  const getAssetTypeIcon = (type: AssetType) => {
    const config = ASSET_TYPE_CONFIG[type];
    return (
      <div
        className={`w-12 h-12 ${config.bgColor} rounded-lg flex items-center justify-center`}
      >
        <span className="text-2xl">{config.icon}</span>
      </div>
    );
  };

  // Calculate stats from assets
  const stats = {
    total: total,
    byType: Object.values(AssetType).reduce((acc, type) => {
      acc[type] = assets.filter((asset) => asset.type === type).length;
      return acc;
    }, {} as Record<AssetType, number>),
    totalFiles: assets.reduce((sum, asset) => sum + asset.files.length, 0),
    totalConversations: assets.reduce(
      (sum, asset) => sum + asset.conversationIds.length,
      0
    ),
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-3xl font-bold text-gray-900">Assets</h1>
        <p className="mt-2 text-gray-600">
          Reusable PDFs, images, videos, and documents that can be attached to
          any conversation.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Assets</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? "..." : stats.total}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Files</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? "..." : stats.totalFiles}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Conversations</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? "..." : stats.totalConversations}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading
                  ? "..."
                  : assets.filter((a) => a.status === AssetStatus.ACTIVE)
                      .length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Create Asset Section */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Create New Asset
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={() => handleCreateAsset()}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                data-testid="create-asset-button"
              >
                Create Asset
              </button>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {Object.values(AssetType).map((type) => {
              const config = ASSET_TYPE_CONFIG[type];
              return (
                <button
                  key={type}
                  onClick={() => handleCreateAsset(type)}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-600 hover:bg-purple-50 transition-colors"
                >
                  <div
                    className={`w-12 h-12 ${config.bgColor} rounded-lg mx-auto mb-3 flex items-center justify-center`}
                  >
                    <span className="text-2xl">{config.icon}</span>
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">
                    {ASSET_TYPE_LABELS[type]}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Create {type.toLowerCase()} asset
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Advanced Search and Filters */}
      <AssetSearchFilters
        filter={filter}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        className="mb-6"
      />

      {/* Asset Library */}
      <div
        className="bg-white rounded-lg shadow border"
        data-testid="assets-page"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                Asset Library
              </h2>
              {assets.length > 0 && selectionStats.anySelected && (
                <span className="ml-3 bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded">
                  {selectionStats.count} selected
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {/* Bulk Action Buttons - Only show when items are selected */}
              {selectionStats.anySelected && (
                <>
                  <button
                    onClick={handleBulkGenerateQRCodes}
                    disabled={
                      bulkQRGenerationInProgress || bulkActionInProgress
                    }
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h4"
                      />
                    </svg>
                    <span>
                      {bulkQRGenerationInProgress
                        ? `Generating... (${qrGenerationProgress.completed}/${qrGenerationProgress.total})`
                        : "Generate QR Codes"}
                    </span>
                  </button>
                  <button
                    onClick={handleBulkDownloadQRCodes}
                    disabled={bulkActionInProgress}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span>Download QR Codes</span>
                  </button>
                  <button
                    onClick={() => setShowArchiveConfirmModal(true)}
                    disabled={bulkActionInProgress}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                      />
                    </svg>
                    <span>Archive</span>
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirmModal(true)}
                    disabled={bulkActionInProgress}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    <span>Delete</span>
                  </button>
                </>
              )}

              <button
                onClick={handleExport}
                disabled={assets.length === 0}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span>Export</span>
              </button>
              <div className="flex border border-gray-300 rounded-lg">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`px-3 py-2 text-sm border-r border-gray-300 ${
                    viewMode === "grid" ? "bg-gray-100" : ""
                  }`}
                  data-testid="view-mode-grid"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`px-3 py-2 text-sm ${
                    viewMode === "list" ? "bg-gray-100" : ""
                  }`}
                  data-testid="view-mode-list"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6">
          {/* Loading State */}
          {loading && assets.length === 0 && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading assets...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error loading assets
              </h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={refetch}
                className="text-purple-600 hover:text-purple-700 font-medium"
              >
                Try again
              </button>
            </div>
          )}

          {/* Assets Grid/List */}
          {!loading && !error && assets.length > 0 && (
            <div>
              {viewMode === "list" ? (
                /* List View with Checkboxes */
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 w-10">
                          <div className="flex items-center">
                            <input
                              id="select-all-checkbox"
                              type="checkbox"
                              className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                              checked={selectionStats.allSelected}
                              onChange={(e) =>
                                handleSelectAllAssets(e.target.checked)
                              }
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Asset
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Type
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Status
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Files
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Conversations
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {assets.map((asset) => {
                        const isSelected = selectedAssetIds.has(asset.id);
                        return (
                          <tr
                            key={asset.id}
                            className={`${
                              isSelected ? "bg-purple-50" : ""
                            } hover:bg-gray-50`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <input
                                  id={`checkbox-${asset.id}`}
                                  type="checkbox"
                                  className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                                  checked={isSelected}
                                  onChange={(e) =>
                                    handleAssetSelection(
                                      asset.id,
                                      e.target.checked
                                    )
                                  }
                                />
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div
                                    className={`w-10 h-10 ${
                                      ASSET_TYPE_CONFIG[asset.type].bgColor
                                    } rounded-lg flex items-center justify-center`}
                                  >
                                    <span className="text-lg text-white">
                                      {ASSET_TYPE_CONFIG[asset.type].icon}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {asset.name}
                                  </div>
                                  {asset.venue && (
                                    <div className="text-xs text-gray-500">
                                      📍 {asset.venue.name}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {ASSET_TYPE_LABELS[asset.type]}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  asset.status === AssetStatus.ACTIVE
                                    ? "bg-green-100 text-green-800"
                                    : asset.status === AssetStatus.INACTIVE
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {asset.status.toLowerCase()}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {getAssetFileSize(asset)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {asset.conversationIds.length}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-3">
                                <button
                                  onClick={() => {
                                    setSelectedAsset(asset);
                                    setShowDetailModal(true);
                                    // Set the active tab to links when opening from QR button
                                    setTimeout(() => {
                                      const linksTab = document.querySelector(
                                        '[data-testid="links-tab"]'
                                      ) as HTMLButtonElement;
                                      if (linksTab) linksTab.click();
                                    }, 100);
                                  }}
                                  className="text-gray-500 hover:text-gray-600 flex items-center space-x-2"
                                  title="Generate QR Codes & Links"
                                >
                                  <svg
                                    className="w-4 h-4 text-gray-600"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM13 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4z"
                                      clipRule="evenodd"
                                    ></path>
                                  </svg>
                                  <span> QR Code</span>
                                </button>

                                <button
                                  onClick={() => handleEditAsset(asset)}
                                  className="text-gray-500 hover:text-gray-600 flex items-center space-x-2"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 
                                      15H9v-2.828l8.586-8.586z"
                                    />
                                  </svg>
                                  <span>Edit</span>
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedAssetIds(new Set([asset.id]));
                                    setShowArchiveConfirmModal(true);
                                  }}
                                  className="text-gray-500 hover:text-gray-600 flex items-center space-x-2"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                                    />
                                  </svg>
                                  <span> Archive</span>
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedAssetIds(new Set([asset.id]));
                                    setShowDeleteConfirmModal(true);
                                  }}
                                  className="text-red-600 hover:text-red-700 flex items-center space-x-2"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                  <span> Delete</span>
                                </button>
                                <button
                                  onClick={() => handleViewAsset(asset)}
                                  className="text-purple-600 hover:text-purple-700"
                                >
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M9 5l7 7-7 7"
                                    ></path>
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                /* Grid View */
                <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {assets.map((asset) => (
                    <div
                      key={asset.id}
                      className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <div className="relative">
                        <div className="absolute top-2 left-2">
                          <input
                            id={`grid-checkbox-${asset.id}`}
                            type="checkbox"
                            className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                            checked={selectedAssetIds.has(asset.id)}
                            onChange={(e) =>
                              handleAssetSelection(asset.id, e.target.checked)
                            }
                          />
                        </div>
                        <div className="aspect-w-16 aspect-h-9 bg-gray-100 flex items-center justify-center">
                          {getAssetTypeIcon(asset.type)}
                        </div>
                      </div>
                      <div className="p-4 flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-medium text-gray-900 truncate">
                            {asset.name}
                          </h3>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              asset.status === AssetStatus.ACTIVE
                                ? "bg-green-100 text-green-800"
                                : asset.status === AssetStatus.INACTIVE
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {asset.status.toLowerCase()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mb-2">
                          {ASSET_TYPE_LABELS[asset.type]} •{" "}
                          {getAssetFileSize(asset)}
                        </p>
                        {asset.venue && (
                          <p className="text-xs text-gray-400 mb-2">
                            📍 {asset.venue.name}
                          </p>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">
                            {asset.conversationIds.length > 0
                              ? `Linked to ${
                                  asset.conversationIds.length
                                } conversation${
                                  asset.conversationIds.length > 1 ? "s" : ""
                                }`
                              : "No conversation linked"}
                          </span>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setSelectedAsset(asset);
                                setShowDetailModal(true);
                                // Set the active tab to links when opening from QR button
                                setTimeout(() => {
                                  const linksTab = document.querySelector(
                                    '[data-testid="links-tab"]'
                                  ) as HTMLButtonElement;
                                  if (linksTab) linksTab.click();
                                }, 100);
                              }}
                              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                              title="Generate QR Codes & Links"
                            >
                              QR Code
                            </button>
                            <button
                              onClick={() => handleViewAsset(asset)}
                              className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleEditAsset(asset)}
                              className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Create New Asset Card */}
                  <button
                    onClick={() => handleCreateAsset()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-600 hover:bg-purple-50 transition-colors cursor-pointer"
                  >
                    <div className="w-12 h-12 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-500">Create new asset</p>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && assets.length === 0 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {Object.keys(filter).length > 0
                  ? "No assets found"
                  : "No assets yet"}
              </h3>
              <p className="text-gray-500 mb-4">
                {Object.keys(filter).length > 0
                  ? "No assets match your current search and filter criteria."
                  : "Get started by creating your first asset."}
              </p>
              {Object.keys(filter).length > 0 ? (
                <button
                  onClick={handleClearFilters}
                  className="text-purple-600 hover:text-purple-700 font-medium"
                >
                  Clear filters
                </button>
              ) : (
                <button
                  onClick={() => handleCreateAsset()}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  data-testid="create-asset-button-alt"
                >
                  Create Asset
                </button>
              )}
            </div>
          )}

          {/* Load More Button */}
          {!loading && !error && assets.length > 0 && hasMore && (
            <div className="text-center mt-6">
              <button
                onClick={loadMore}
                disabled={loading}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {loading ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Asset Creation Modal */}
      <AssetFormModal
        isOpen={showCreateModal}
        onClose={handleModalClose}
        onSubmit={handleAssetSubmit}
        isEditing={!!selectedAsset}
        initialData={
          selectedAsset ||
          (selectedAssetType
            ? ({ type: selectedAssetType } as Partial<Asset>)
            : ({} as Partial<Asset>))
        }
      />

      {/* Asset Detail Modal */}
      <AssetDetailModal
        asset={selectedAsset}
        isOpen={showDetailModal}
        onClose={handleDetailModalClose}
        onEdit={handleEditAsset}
      />

      {/* Asset Export Modal */}
      {showExportModal && (
        <AssetExport
          filter={filter}
          totalAssets={total}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Dialog
        isOpen={showDeleteConfirmModal}
        onClose={() => setShowDeleteConfirmModal(false)}
        title="Delete Assets"
      >
        <div className="p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="mb-5 text-lg font-medium text-center text-gray-900">
            Are you sure you want to delete{" "}
            {selectedAssetIds.size === 1
              ? "this asset"
              : `these ${selectedAssetIds.size} assets`}
            ?
          </h3>
          <p className="mb-5 text-sm text-center text-gray-500">
            This action cannot be undone. This will permanently delete the
            selected {selectedAssetIds.size === 1 ? "asset" : "assets"} and
            remove all associated data.
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setShowDeleteConfirmModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteAssets}
              disabled={bulkActionInProgress}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {bulkActionInProgress ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Archive Confirmation Modal */}
      <Dialog
        isOpen={showArchiveConfirmModal}
        onClose={() => setShowArchiveConfirmModal(false)}
        title="Archive Assets"
      >
        <div className="p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-yellow-100 rounded-full">
            <svg
              className="w-6 h-6 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
              />
            </svg>
          </div>
          <h3 className="mb-5 text-lg font-medium text-center text-gray-900">
            Are you sure you want to archive{" "}
            {selectedAssetIds.size === 1
              ? "this asset"
              : `these ${selectedAssetIds.size} assets`}
            ?
          </h3>
          <p className="mb-5 text-sm text-center text-gray-500">
            Archived assets will be hidden from the main view but can be
            restored later.
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setShowArchiveConfirmModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleArchiveAssets}
              disabled={bulkActionInProgress}
              className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700 disabled:opacity-50"
            >
              {bulkActionInProgress ? "Archiving..." : "Archive"}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Bulk QR Generation Progress Modal */}
      <Dialog isOpen={bulkQRGenerationInProgress} onClose={() => {}}>
        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg
                className="h-6 w-6 text-blue-600 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Generating QR Codes
              </h3>
              <div className="mt-4">
                <div className="bg-gray-200 rounded-full h-2 mb-4">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${
                        qrGenerationProgress.total > 0
                          ? ((qrGenerationProgress.completed +
                              qrGenerationProgress.skipped +
                              qrGenerationProgress.errors) /
                              qrGenerationProgress.total) *
                            100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>
                    Progress:{" "}
                    {qrGenerationProgress.completed +
                      qrGenerationProgress.skipped +
                      qrGenerationProgress.errors}{" "}
                    of {qrGenerationProgress.total} assets processed
                  </p>
                  {qrGenerationProgress.completed > 0 && (
                    <p className="text-green-600">
                      ✅ {qrGenerationProgress.completed} QR codes generated
                    </p>
                  )}
                  {qrGenerationProgress.skipped > 0 && (
                    <p className="text-yellow-600">
                      ⏭️ {qrGenerationProgress.skipped} assets skipped (already
                      have QR codes)
                    </p>
                  )}
                  {qrGenerationProgress.errors > 0 && (
                    <p className="text-red-600">
                      ❌ {qrGenerationProgress.errors} errors occurred
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
